import { observable, action, runInAction, computed } from 'mobx';
import {
  FullSurgeryGuideDTO,
  GroupedSurgeryGuideMaterialsDTO,
  CreateSurgeryGuideMaterialDTO,
  UpdateSurgeryGuideMaterialDTO,
  UpdateSortSurgeryGuideMaterialsDTO,
  SurgeryGuideMaterialLikeDTO
} from 'dto/surgeryGuide';
import { SetUsedMaterialDTO, UsedMaterialDTO } from 'dto/usedMaterial';
import {
  GroupedSurgeryGuideProcedureDTO,
  SurgeryProcedureDTO,
  CreateSurgeryGuideProcedureDTO,
  UpdateSurgeryGuideProcedureDTO,
  UpdateSortSurgeryGuideProceduresDTO
} from 'dto/surgeryGuideProcedure';
import {
  getSurgeryGuide,
  getSurgeryGuideProcedures,
  createSurgeryGuideProcedure,
  updateSurgeryGuideProcedure,
  reorderSurgeryGuideProcedures,
  reorderSurgeryGuideMaterials,
  getSurgeryGuideMaterials,
  createSurgeryGuideMaterial,
  deleteSurgeryGuide,
  updateSurgeryGuideMaterial,
  setUsedSurgeryGuideMaterial
} from 'api/surgeryGuide';
import { BriefingDTO } from 'dto/briefing';
import { MaterialType } from 'dto/material';
import { getSurgeryBriefing } from 'api/surgery';
import DomainStore from './domainStore';
import LoadingStore from './loadingStore';
import { AnyMaterial } from './searchStore';

export default class SurgeryGuideStore {
  @observable
  private domainStore: DomainStore;

  @observable
  private loadingStore: LoadingStore;

  @observable
  surgeryGuide?: FullSurgeryGuideDTO;

  @observable
  briefing?: BriefingDTO;

  @observable
  groupedGuideProcedures: GroupedSurgeryGuideProcedureDTO[] = [];

  @observable
  checkableGuideProcedures: SurgeryProcedureDTO[] = [];

  @observable
  guideMaterials: GroupedSurgeryGuideMaterialsDTO[] = [];

  @observable
  selectedSurgeryProcedure?: SurgeryProcedureDTO;

  @observable
  isProcedureDrawerOpen = false;

  @observable
  isEditLotNumberFlyoutOpen = false;

  @observable
  procedureIndexInView = 0;

  @observable
  selectedProcedureChapter?: string;

  @observable
  selectedSurgeryGuideToEdit?: SurgeryGuideMaterialLikeDTO;

  @observable
  selectedUsedMaterialToEdit?: UsedMaterialDTO;

  constructor(domainStore: DomainStore, loadingStore: LoadingStore) {
    this.domainStore = domainStore;
    this.loadingStore = loadingStore;
  }

  @computed
  get selectedGroup(): GroupedSurgeryGuideProcedureDTO | null {
    const group = this.groupedGuideProcedures.find(
      groupedGuideProcedure => groupedGuideProcedure.chapter === this.selectedProcedureChapter
    );

    if (!group) {
      return null;
    }

    group.surgeryGuideProcedures = group.surgeryGuideProcedures.filter(surgeryGuideProcedure => surgeryGuideProcedure.content.isCheckable);

    return group;
  }

  async createSurgeryGuideProcedure(newGuideProcedure: CreateSurgeryGuideProcedureDTO) {
    const procedure = await this.loadingStore.withFlyoutLoadingBar(() => createSurgeryGuideProcedure(newGuideProcedure));
    if (this.surgeryGuide) {
      this.loadGuideProcedures(this.surgeryGuide.surgeryGuideId);
    }
    return procedure;
  }

  async updateSurgeryGuideProcedure(guideProcedure: UpdateSurgeryGuideProcedureDTO) {
    const procedure = await this.loadingStore.withFlyoutLoadingBar(() => updateSurgeryGuideProcedure(guideProcedure));
    if (this.surgeryGuide) {
      this.loadGuideProcedures(this.surgeryGuide.surgeryGuideId);
    }
    return procedure;
  }

  async loadGuide(surgeryGuideId: string) {
    const surgeryGuide = await this.loadingStore.withLoadingBar(() => getSurgeryGuide(surgeryGuideId));
    runInAction(() => {
      this.surgeryGuide = surgeryGuide;
    });
  }

  async loadBriefing(briefingId: string) {
    const briefing = await this.loadingStore.withLoadingBar(() => getSurgeryBriefing({ briefingId, includeStatistic: false }));
    runInAction(() => {
      this.briefing = briefing;
    });
  }

  async loadGuideProcedures(surgeryGuideId: string) {
    const groupedGuideProcedures: GroupedSurgeryGuideProcedureDTO[] = await this.loadingStore.withOnlyLoadingBar(() =>
      getSurgeryGuideProcedures({ surgeryGuideId })
    );
    let checkableGuideProcedures: SurgeryProcedureDTO[] = [];
    groupedGuideProcedures.forEach(groupedGuideProcedure => {
      checkableGuideProcedures = [
        ...checkableGuideProcedures,
        ...groupedGuideProcedure.surgeryGuideProcedures.filter(surgeryGuideProcedure => surgeryGuideProcedure.content.isCheckable)
      ];
    });
    runInAction(() => {
      this.groupedGuideProcedures = groupedGuideProcedures;
      this.checkableGuideProcedures = checkableGuideProcedures;
    });
  }

  async loadGuideMaterials(surgeryGuideId: string) {
    const guideMaterials = await this.loadingStore.withOnlyLoadingBar(() => getSurgeryGuideMaterials(surgeryGuideId));
    runInAction(() => {
      this.guideMaterials = guideMaterials;
    });
  }

  async refreshGuideMaterials() {
    const guideMaterials = await this.loadingStore.withLoadingBar(() => {
      if (this.surgeryGuide) {
        return getSurgeryGuideMaterials(this.surgeryGuide.surgeryGuideId);
      }
      return Promise.resolve([]);
    });
    runInAction(() => {
      this.guideMaterials = guideMaterials;
    });
  }

  async updateProcedure(procedure: UpdateSurgeryGuideProcedureDTO) {
    await this.loadingStore.withFlyoutLoadingBar(() => updateSurgeryGuideProcedure(procedure));
    if (this.surgeryGuide) {
      this.loadGuideProcedures(this.surgeryGuide.surgeryGuideId);
    }
  }

  async reorderProcedures(positions: UpdateSortSurgeryGuideProceduresDTO[]) {
    await this.loadingStore.withLoadingBar(() => reorderSurgeryGuideProcedures(positions));
    if (this.surgeryGuide) {
      this.loadGuideProcedures(this.surgeryGuide.surgeryGuideId);
    }
  }

  async reorderMaterials(positions: UpdateSortSurgeryGuideMaterialsDTO[]) {
    await this.loadingStore.withLoadingBar(() => reorderSurgeryGuideMaterials(positions));
    if (this.surgeryGuide) {
      this.loadGuideMaterials(this.surgeryGuide.surgeryGuideId);
    }
  }

  async addMaterialToGuide(materialLike: AnyMaterial) {
    if (!this.surgeryGuide) {
      return Promise.resolve();
    }
    const categoryId = this.guideMaterials[0].categoryId;
    let guideMaterial: CreateSurgeryGuideMaterialDTO | undefined;

    // check which materialLike --> if materialSet different way of adding (see SearchMaterialSetItem)
    if (materialLike.material) {
      // checks if searchresult is a package or only a single material
      if (materialLike.material.materialType === MaterialType.instrument) {
        throw new Error('instruments cannot be added as material');
      } else {
        guideMaterial = {
          surgeryGuideId: this.surgeryGuide.surgeryGuideId,
          materialId: materialLike.material.materialId,
          guideMaterialCategoryId: categoryId,
          amount: 1,
          notes: {
            unpackOnInstruction: false
          }
        };
      }
    }
    if (materialLike.pack) {
      guideMaterial = {
        surgeryGuideId: this.surgeryGuide.surgeryGuideId,
        templateId: materialLike.pack.templateId,
        guideMaterialCategoryId: categoryId,
        amount: 1,
        notes: {
          unpackOnInstruction: false
        }
      };
    }
    if (materialLike.materialSet)
      guideMaterial = {
        surgeryGuideId: this.surgeryGuide.surgeryGuideId,
        materialSetId: materialLike.materialSet.materialSetId,
        guideMaterialCategoryId: categoryId,
        amount: 1,
        notes: {
          unpackOnInstruction: false
        }
      };

    if (guideMaterial && this.surgeryGuide) {
      await createSurgeryGuideMaterial(guideMaterial);
      this.loadGuideMaterials(this.surgeryGuide.surgeryGuideId);
    }

    return Promise.resolve();
  }

  async deleteSurgeryGuide(surgeryGuideId: string) {
    await this.loadingStore.withLoadingBar(() => deleteSurgeryGuide(surgeryGuideId));
    if (this.surgeryGuide) {
      this.loadGuideMaterials(this.surgeryGuide.surgeryGuideId);
    }
  }

  async updateSurgeryGuide(surgeryGuide: UpdateSurgeryGuideMaterialDTO) {
    await this.loadingStore.withLoadingBar(() => updateSurgeryGuideMaterial(surgeryGuide));
    if (this.surgeryGuide) {
      this.loadGuideMaterials(this.surgeryGuide.surgeryGuideId);
    }
  }

  async setUsedMaterial(usedMaterial: SetUsedMaterialDTO) {
    await this.loadingStore.withOnlyLoadingBar(() => setUsedSurgeryGuideMaterial(usedMaterial));
    if (this.surgeryGuide) {
      this.loadGuideMaterials(this.surgeryGuide.surgeryGuideId);
    }
  }

  @action
  setSelectedSurgeryProcedure(selectedSurgeryProcedure?: SurgeryProcedureDTO) {
    this.selectedSurgeryProcedure = selectedSurgeryProcedure;
  }

  @action
  clearSelectedSurgeryProcedure() {
    this.selectedSurgeryProcedure = undefined;
  }

  @action
  setIsProcedureDrawerOpen(status: boolean) {
    this.isProcedureDrawerOpen = status;
  }

  @action
  setIsEditLotNumberFlyoutOpen(status: boolean, surgeryGuide?: SurgeryGuideMaterialLikeDTO, usedMaterial?: UsedMaterialDTO) {
    this.isEditLotNumberFlyoutOpen = status;
    this.selectedSurgeryGuideToEdit = surgeryGuide;
    this.selectedUsedMaterialToEdit = usedMaterial;
  }

  @action
  setProcedureIndexInView(procedureIndexInView: number) {
    this.procedureIndexInView = procedureIndexInView;
  }

  @action
  selectProcedureChapter(chapter: string) {
    if (this.selectedProcedureChapter === chapter) {
      return;
    }
    this.selectedProcedureChapter = chapter;
    this.procedureIndexInView = 0;
  }

  @action
  clearProcedures() {
    this.groupedGuideProcedures = [];
  }

  @action
  clearMaterials() {
    this.guideMaterials = [];
  }
}
